/**
 * Store Locator - Main JavaScript functionality
 * Handles map integration, search functionality, and mobile responsiveness
 */

import { LocationsService, escapeHtml } from './locations-service.js';
import { createTranslator } from './translator.js';

// Utility functions
function debounce(func, wait) {
  let timeout;

  const debouncedFunction = function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      timeout = null;
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };

  // Add cancel method to abort pending execution
  debouncedFunction.cancel = function() {
    clearTimeout(timeout);
    timeout = null;
  };

  return debouncedFunction;
}

/**
 * Store Locator Component
 */
class StoreLocator {
  constructor(element) {
    this.element = element;
    this.loadConfiguration();
    this.initializeState();
    this.bindElements();

    // Initialize component
    this.init();
  }

  /**
   * Load configuration from data attributes
   */
  loadConfiguration() {
    this.brand = this.element.dataset.brand || 'bakers';
    this.shopDomain = this.element.dataset.shopDomain;
    this.storefrontApiVersion = this.element.dataset.storefrontApiVersion || '2025-10';
    this.storefrontAccessToken = this.element.dataset.storefrontAccessToken;

    // Map provider configuration
    this.mapProviderType = this.element.dataset.mapProvider || 'mapbox';
    this.mapAccessToken = this.element.dataset.mapAccessToken;
    this.mapProvider = null;
    this.unitSystem = this.element.dataset.unitSystem || 'metric';
    this.locale = this.element.dataset.locale || 'en-AU';

    this.defaultLat = parseFloat(this.element.dataset.defaultLat) || -25.2744;
    this.defaultLng = parseFloat(this.element.dataset.defaultLng) || 133.7751;
    this.defaultZoom = parseInt(this.element.dataset.defaultZoom) || 10;
    this.searchZoom = parseInt(this.element.dataset.searchZoom) || 14;
    this.defaultTimezone = this.element.dataset.defaultTimezone || 'UTC';
    this.locationsJsonUrl = this.element.dataset.locationsJsonUrl; // Static locations JSON URL
    this.resultsPerPage = parseInt(this.element.dataset.resultsPerPage) || 20;

    // Map controls configuration
    this.mapControls = {
      zoom: this.element.dataset.mapControlZoom || 'none',
      fullscreen: this.element.dataset.mapControlFullscreen || 'none'
    };

    // Map gestures configuration
    const gestureMode = this.element.dataset.mapGesture || 'cooperative';
    const gesturesEnabled = gestureMode !== 'none';

    this.mapGestures = {
      zoom: gestureMode,
      doubleClickZoom: gesturesEnabled,
      dragPan: gesturesEnabled,
      keyboard: gesturesEnabled,
      touchZoomRotate: gesturesEnabled
    };
    this.showPagination = this.element.dataset.showPagination === 'true';
    this.enableGeolocation = this.element.dataset.enableGeolocation === 'true';
    this.countryCode = this.element.dataset.countryCode || 'au';
    this.deliveryIconsBaseUrl = this.element.dataset.deliveryIconsBaseUrl || '';
    // Delivery service logos from theme settings (data attributes are converted to camelCase)
    this.deliveryLogos = {
      'ubereats': this.element.dataset.deliveryLogoUbereats || '',
      'menulog': this.element.dataset.deliveryLogoMenulog || '',
      'doordash': this.element.dataset.deliveryLogoDoordash || '',
      'skipthedishes': this.element.dataset.deliveryLogoSkipthedishes || ''
    };

    // Cache template reference
    this.storeCardTemplate = document.getElementById('store-card-template');
  }

  /**
   * Initialize services (LocationsService and MapProvider)
   */
  async initializeServices() {
    // Initialize translator (empty namespace to support multiple top-level namespaces)
    this.translator = createTranslator('store-locator-translations', '');

    // Initialize locations service with translator
    this.locationsService = new LocationsService({
      shopDomain: this.shopDomain,
      apiVersion: this.storefrontApiVersion,
      accessToken: this.storefrontAccessToken,
      defaultTimezone: this.defaultTimezone,
      translator: this.translator,
      locationsJsonUrl: this.locationsJsonUrl
    });

    // Dynamically import and initialize map provider
    if (this.mapAccessToken) {
      const mapConfig = {
        token: this.mapAccessToken,
        brand: this.brand,
        defaultLat: this.defaultLat,
        defaultLng: this.defaultLng,
        defaultZoom: this.defaultZoom,
        markerUrl: this.element.dataset.markerUrl,
        popupTemplate: this.element.querySelector('template[name="marker-popup"]'),
        controls: this.mapControls,
        gestures: this.mapGestures
      };

      // Dynamic provider import based on configuration
      // Use URLs from data attributes to ensure proper cache busting (includes ?v= parameter)
      if (this.mapProviderType === 'google') {
        const providerUrl = this.element.dataset.googleProviderUrl || './map-provider-google.js';
        const { GoogleMapsProvider } = await import(providerUrl);
        this.mapProvider = new GoogleMapsProvider(mapConfig);
      } else {
        // Default to Mapbox
        const providerUrl = this.element.dataset.mapboxProviderUrl || './map-provider-mapbox.js';
        const { MapboxProvider } = await import(providerUrl);
        this.mapProvider = new MapboxProvider(mapConfig);
      }
    }
  }

  /**
   * Initialize component state
   */
  initializeState() {
    this.map = null;
    this.markers = [];
    this.clusterGroup = null;
    this.currentResults = [];
    this.filteredLocations = []; // All filtered results (for map and count)
    this.allLocations = []; // Cache all locations for viewport filtering
    this.visibleLocations = []; // Locations currently visible in viewport
    this.currentCursor = null;
    this.hasNextPage = false;
    this.isLoading = false;
    this.currentPage = 1; // Client-side pagination

    // Viewport optimization state
    this.lastBounds = null;
    this.lastZoom = null;
    this.isUpdatingFromBounds = false;

    this.currentFilters = {
      services: [],
      features: [],
      searchQuery: '',
      userLocation: null
    };

    // Autocomplete state
    this.autocompleteDropdown = null;
    this.autocompleteResults = [];
    this.isAutocompleteOpen = false;
    this.isInitialLoad = true; // Track if this is the first load
  }

  /**
   * Bind DOM elements
   */
  bindElements() {
    this.searchInput = this.element.querySelector('#location-search');
    this.searchButton = this.element.querySelector('.search-button');
    this.clearSearchButton = this.element.querySelector('.clear-search-button');
    this.geolocationButton = this.element.querySelector('.geolocation-button');
    this.resetMapButton = this.element.querySelector('.reset-map-button');
    this.resultsContainer = this.element.querySelector('#results-container');
    this.resultsStatus = this.element.querySelector('#results-status');
    this.storeMap = this.element.querySelector('#store-map');
    this.resultsList = this.element.querySelector('#results-list');
    this.loadingState = this.element.querySelector('#loading-state');
    this.errorState = this.element.querySelector('#error-state');
    this.emptyState = this.element.querySelector('#empty-state');
    this.paginationNav = this.element.querySelector('#pagination-nav');
    this.mapContainer = this.element.querySelector('.store-locator__map-container');
    this.sidebar = this.element.querySelector('.store-locator__sidebar');
    this.filterDisclosure = this.element.querySelector('.filter-disclosure');
    this.mobileMapToggle = this.element.querySelector('.mobile-map-toggle-button');
    this.mobileBakeriesCount = this.element.querySelector('.mobile-bakeries-count');
    this.storeItemTemplate = this.element.querySelector('template[name="autocomplete-store-item"]');
    this.locationItemTemplate = this.element.querySelector('template[name="autocomplete-location-item"]');
  }

  /**
   * Initialize store locator functionality
   */
  async init() {
    try {
      await this.initializeServices();
      this.initializeAutocomplete();
      this.bindEvents(); // Includes mobile map toggle handler

      if (this.mapProvider) {
        await this.initializeMap();
        this.mapProvider.setupSpiderfyBehavior(); // Initialize spiderfy behavior
      } else {
        // If no map, load results immediately
        await this.loadInitialResults();
      }

      // Generate dynamic filter options after locations are loaded
      this.generateDeliveryServiceFilters();

      // Announce ready state to screen readers
      this.announceToScreenReader(this.translator.translate('accessibility.store_locator.initialized'));

    } catch (error) {
      console.error('Failed to initialize store locator:', error);
      this.showErrorState('Failed to load store locator. Please refresh the page.');
    }
  }

  /**
   * Initialize autocomplete dropdown
   */
  initializeAutocomplete() {
    if (!this.searchInput) return;

    // Create autocomplete dropdown
    this.autocompleteDropdown = document.createElement('div');
    this.autocompleteDropdown.className = 'autocomplete-dropdown';
    this.autocompleteDropdown.setAttribute('role', 'listbox');
    this.autocompleteDropdown.hidden = true;

    // Append dropdown inside the search wrapper
    const searchWrapper = this.searchInput.closest('.search-input-wrapper') || this.searchInput.parentElement;
    searchWrapper.appendChild(this.autocompleteDropdown);

    // Store wrapper reference
    this.searchWrapper = searchWrapper;

    // Create debounced search handler
    this.debouncedAutocomplete = debounce(async (query) => {
      await this.performAutocomplete(query);
    }, 300);
  }

  /**
   * Perform autocomplete search (stores + locations)
   */
  async performAutocomplete(query) {
    if (!query || query.length < 2) {
      this.closeAutocomplete();
      return;
    }

    try {
      // Search both stores and locations in parallel
      const [storeResults, locationResults] = await Promise.all([
        this.searchStoresByName(query),
        this.searchLocationsByProvider(query)
      ]);

      // Combine results (stores first, then locations), max 5 total
      this.autocompleteResults = [
        ...storeResults.map(store => ({ type: 'store', data: store })),
        ...locationResults.map(location => ({ type: 'location', data: location }))
      ].slice(0, 5);

      this.renderAutocompleteResults();
      this.openAutocomplete();

    } catch (error) {
      console.error('Autocomplete failed:', error);
      this.closeAutocomplete();
    }
  }

  /**
   * Search stores by name using Shopify Storefront API
   */
  async searchStoresByName(query) {
    try {
      return await this.locationsService.searchLocationsByName(query);
    } catch (error) {
      console.error('Store name search failed:', error);
      return [];
    }
  }

  /**
   * Search locations using map provider (Mapbox or Google)
   */
  async searchLocationsByProvider(query) {
    if (!this.mapProvider) return [];

    try {
      return await this.mapProvider.searchPlaces(query, this.countryCode);
    } catch (error) {
      console.error('Location search failed:', error);
      return [];
    }
  }

  /**
   * Render autocomplete results
   */
  renderAutocompleteResults() {
    if (!this.autocompleteDropdown) return;

    this.autocompleteDropdown.innerHTML = '';

    if (this.autocompleteResults.length === 0) {
      this.closeAutocomplete();
      return;
    }

    this.autocompleteResults.forEach((result, index) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'autocomplete-item';
      item.setAttribute('role', 'option');
      item.setAttribute('data-index', index);

      if (result.type === 'store') {
        // Store result - use template
        const store = result.data;
        const storeName = store.metafields?.publicStoreName || store.name;
        const address = store.address;

        // Build full address for subtitle
        const addressParts = [address.address1];
        if (address.address2) {
          addressParts.push(address.address2);
        }
        const fullAddress = addressParts.join(', ');

        // Clone template and populate
        if (this.storeItemTemplate) {
          const template = this.storeItemTemplate.content.cloneNode(true);
          const storeNameEl = template.querySelector('[data-store-name]');
          const storeAddressEl = template.querySelector('[data-store-address]');

          if (storeNameEl) storeNameEl.textContent = storeName;
          if (storeAddressEl) storeAddressEl.textContent = fullAddress;

          item.appendChild(template);
        }
      } else {
        // Location result - use template
        const location = result.data;
        const placeName = location.place_name || location.text;

        // Clone template and populate
        if (this.locationItemTemplate) {
          const template = this.locationItemTemplate.content.cloneNode(true);
          const locationNameEl = template.querySelector('[data-location-name]');

          if (locationNameEl) locationNameEl.textContent = placeName;

          item.appendChild(template);
        }
      }

      // Click handler (also triggered by Enter/Space automatically)
      item.addEventListener('click', () => {
        this.selectAutocompleteResult(index);
      });

      // Escape key handler to close dropdown
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeAutocomplete();
          this.searchInput.focus();
        }
      });

      this.autocompleteDropdown.appendChild(item);
    });
  }

  /**
   * Select autocomplete result
   */
  async selectAutocompleteResult(index) {
    if (index < 0 || index >= this.autocompleteResults.length) return;

    const result = this.autocompleteResults[index];
    this.closeAutocomplete();

    if (result.type === 'store') {
      // Store selected - zoom to store and load nearby stores
      const store = result.data;
      const lat = store.coordinates?.latitude;
      const lng = store.coordinates?.longitude;

      if (lat && lng) {
        // Update search input with store name
        const storeName = store.metafields?.publicStoreName || store.name;
        if (this.searchInput) {
          this.searchInput.value = storeName;
        }

        // Store user location for distance calculations
        this.currentFilters.userLocation = { lat, lng };

        // Zoom to store location
        if (this.mapProvider) {
          this.mapProvider.flyTo(lat, lng, 15);
        }

        // Show reset map button
        this.toggleResetMapButton(true);

        // Viewport optimization will automatically update the store list
      }
    } else {
      // Location selected - zoom to location and load stores
      const location = result.data;
      const placeName = location.place_name || location.text;

      // Update search input with location name
      if (this.searchInput) {
        this.searchInput.value = placeName;
      }

      // If location has coordinates (Mapbox), use them directly
      if (location.center && Array.isArray(location.center)) {
        const [lng, lat] = location.center;

        // Store user location for distance calculations
        this.currentFilters.userLocation = { lat, lng };

        // Show user location marker
        this.mapProvider?.addUserLocationMarker(lat, lng);

        // Update distance displays on all store cards
        this.updateAllDistances();

        // Zoom to location
        if (this.mapProvider) {
           // Use bbox if available for better framing
           if (location.bbox) {
             const [minLng, minLat, maxLng, maxLat] = location.bbox;
            // Create virtual locations for fitBounds
            const bboxLocations = [
              { coordinates: { latitude: minLat, longitude: minLng } },
              { coordinates: { latitude: maxLat, longitude: maxLng } }
            ];
            this.mapProvider.fitBounds(bboxLocations);
           } else {
             this.mapProvider.flyTo(lat, lng, this.searchZoom);
           }
         }

        // Show reset map button
        this.toggleResetMapButton(true);

        // Viewport optimization will automatically update the store list
      } else {
        // Google Places result - needs geocoding
        // Use place_id if available, otherwise use place name
        const query = location.place_id || placeName;

        // Geocode and fly to location
        if (this.mapProvider) {
          try {
            const result = await this.mapProvider.geocode(query, this.countryCode);

            if (result && result.lat && result.lng) {
              // Store user location for distance calculations
              this.currentFilters.userLocation = { lat: result.lat, lng: result.lng };

              // Show user location marker
              this.mapProvider?.addUserLocationMarker(result.lat, result.lng);

              // Update distance displays on all store cards
              this.updateAllDistances();

              // Use bbox for better framing if available (especially for states/regions)
              if (result.bbox) {
                const [minLng, minLat, maxLng, maxLat] = result.bbox;
                // Create virtual locations for fitBounds
                const bboxLocations = [
                  { coordinates: { latitude: minLat, longitude: minLng } },
                  { coordinates: { latitude: maxLat, longitude: maxLng } }
                ];
                this.mapProvider.fitBounds(bboxLocations);
              } else {
                // No bbox available, use search zoom level
                this.mapProvider.flyTo(result.lat, result.lng, this.searchZoom);
              }

              // Show reset map button
              this.toggleResetMapButton(true);

              // Viewport optimization will automatically update the store list
            } else {
              console.warn('Unable to geocode location');
              this.announceToScreenReader(this.translator.translate('accessibility.store_locator.location_not_found'));
            }
          } catch (error) {
            console.error('Geocoding failed:', error);
            this.announceToScreenReader(this.translator.translate('accessibility.store_locator.location_search_failed'));
          }
        }
      }
    }
  }


  /**
   * Open autocomplete dropdown
   */
  openAutocomplete() {
    if (!this.autocompleteDropdown || this.autocompleteResults.length === 0) return;

    this.autocompleteDropdown.hidden = false;
    this.isAutocompleteOpen = true;

    // Add class to wrapper for styling
    if (this.searchWrapper) {
      this.searchWrapper.classList.add('autocomplete-open');
    }
  }

  /**
   * Close autocomplete dropdown
   */
  closeAutocomplete() {
    if (!this.autocompleteDropdown) return;

    this.autocompleteDropdown.hidden = true;
    this.isAutocompleteOpen = false;

    // Remove class from wrapper
    if (this.searchWrapper) {
      this.searchWrapper.classList.remove('autocomplete-open');
    }
  }

  /**
   * Initialize map using provider
   */
  async initializeMap() {
    if (!this.mapProvider || !this.storeMap) return;

    // Initialize map through provider
    this.map = await this.mapProvider.initialize(
      this.storeMap,
      () => this.handleBoundsChanged(),
      (zoom) => this.handleZoomChanged(zoom)
    );

    // Setup accessibility
    this.setupMapAccessibility();

    // Load initial results after map is ready
    await this.loadInitialResults();
  }

  /**
   * Setup map accessibility features
   */
  setupMapAccessibility() {
    const mapContainer = this.storeMap;

    // Add keyboard event handlers
    mapContainer.addEventListener('keydown', (e) => {
      this.handleMapKeydown(e);
    });

    // Add accessible description
    mapContainer.setAttribute('aria-describedby', 'map-description');

    const description = document.createElement('div');
    description.id = 'map-description';
    description.className = 'visually-hidden';
    description.textContent = this.translator.translate('accessibility.store_locator.map_description');
    mapContainer.appendChild(description);
  }

  /**
   * Handle map bounds changes with performance optimization
   * Called by map provider after debouncing
   */
  handleBoundsChanged() {
    if (this.isUpdatingFromBounds) return;
    if (!this.mapProvider) return;

    const bounds = this.mapProvider.getBounds();
    const zoom = this.mapProvider.getZoom();

    // Only update if significant viewport change
    if (this.hasSignificantViewChange(bounds, zoom)) {
      this.currentPage = 1; // Reset pagination when viewport changes

      // Only update sidebar - map markers are already rendered and handled by provider
      this.updateSidebarFromBounds();
      this.lastBounds = bounds;
      this.lastZoom = zoom;
    }
  }

  /**
   * Handle zoom changes
   * Called by map provider
   */
  handleZoomChanged(zoom) {
    this.announceZoomChange(zoom);
  }

  /**
   * Check if viewport change is significant enough to update
   */
  hasSignificantViewChange(newBounds, newZoom) {
    if (!this.lastBounds || !this.lastZoom) return true;

    const zoomDiff = Math.abs(newZoom - this.lastZoom);
    const boundsChanged = !this.boundsOverlapSignificantly(this.lastBounds, newBounds);

    return zoomDiff > 0.5 || boundsChanged;
  }

  /**
   * Check if bounds overlap significantly
   */
  boundsOverlapSignificantly(bounds1, bounds2, threshold = 0.8) {
    const area1 = (bounds1.getEast() - bounds1.getWest()) * (bounds1.getNorth() - bounds1.getSouth());
    const area2 = (bounds2.getEast() - bounds2.getWest()) * (bounds2.getNorth() - bounds2.getSouth());

    const intersection = Math.max(0,
      Math.min(bounds1.getEast(), bounds2.getEast()) - Math.max(bounds1.getWest(), bounds2.getWest())
    ) * Math.max(0,
      Math.min(bounds1.getNorth(), bounds2.getNorth()) - Math.max(bounds1.getSouth(), bounds2.getSouth())
    );

    const union = area1 + area2 - intersection;
    return union > 0 ? (intersection / union) >= threshold : false;
  }

  /**
   * Update sidebar to show locations visible in current map bounds
   */
  updateSidebarFromBounds() {
    if (!this.mapProvider || this.isUpdatingFromBounds) return;

    // If locations haven't been loaded yet, show loading state
    if (this.allLocations.length === 0) {
      if (!this.isLoading) {
        // Trigger loading if not already loading
        this.loadResults(true);
      }
      // Show loading state while waiting for locations
      this.showLoadingState();
      return;
    }

    // Hide loading state once locations are loaded
    this.hideLoadingState();

    const bounds = this.mapProvider.getBounds();

    // Get all filtered locations within current viewport
    this.visibleLocations = this.filteredLocations.filter(location => {
      const lng = location.coordinates?.longitude || location.lng;
      const lat = location.coordinates?.latitude || location.lat;

      // Skip locations with invalid coordinates
      if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
        console.warn('Location has invalid coordinates:', location.name, { lat, lng });
        return false;
      }

      return bounds.contains({ lat, lng });
    });

    // Sort by distance if user location is available
    if (this.currentFilters.userLocation) {
      this.visibleLocations = this.sortByDistance(this.visibleLocations, this.currentFilters.userLocation);
    }

    // Apply pagination to viewport-filtered results (if enabled)
    if (this.showPagination) {
      this.currentResults = this.visibleLocations.slice(0, this.currentPage * this.resultsPerPage);
    } else {
      // Show all visible locations when pagination is disabled
      this.currentResults = this.visibleLocations;
    }

    // Update sidebar with paginated viewport results
    this.renderResults(this.currentResults);

    // Update pagination for viewport-filtered results
    this.renderPagination();

    // Update all status displays (desktop + mobile overlay)
    this.updateStatus();
  }

  /**
   * Render results in sidebar
   */
  renderResults(locations) {
    if (!this.resultsList) return;

    this.resultsList.innerHTML = '';

    locations.forEach((location, index) => {
      const locationElement = this.createStoreListItem(location, index);
      this.resultsList.appendChild(locationElement);
    });

    // Update visibility states
    this.setStateVisibility({
      loading: false,
      error: false,
      empty: locations.length === 0,
      results: locations.length > 0
    });
  }

  /**
   * Render pagination for viewport-filtered results
   */
  renderPagination() {
    if (!this.showPagination || !this.paginationNav || !this.visibleLocations) return;

    const totalCount = this.visibleLocations.length;
    const displayedCount = Math.min(this.currentPage * this.resultsPerPage, totalCount);
    const hasMore = displayedCount < totalCount;

    if (!hasMore) {
      this.paginationNav.setAttribute('aria-hidden', 'true');
      this.paginationNav.innerHTML = '';
      return;
    }

    const remainingCount = totalCount - displayedCount;
    const ariaLabel = this.translator.translate('accessibility.store_locator.load_more_bakeries', { count: remainingCount });
    const buttonText = this.translator.translate('store_locator.results.load_more', { count: remainingCount });

    const paginationHTML = `
      <button type="button" class="button pagination-button pagination-next" aria-label="${ariaLabel}">
        ${buttonText}
      </button>
    `;

    this.paginationNav.innerHTML = paginationHTML;
    this.paginationNav.setAttribute('aria-hidden', 'false');

    // Bind click event to load more button
    const loadMoreBtn = this.paginationNav.querySelector('.pagination-next');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', () => this.loadMore());
    }
  }

  /**
   * Load more viewport-filtered results
   */
  loadMore() {
    this.currentPage++;
    this.updateSidebarFromBounds();
  }

  /**
   * Announce zoom level changes to screen readers
   */
  announceZoomChange(zoom) {
    let zoomDescription = '';
    if (zoom <= 8) {
      zoomDescription = this.translator.translate('accessibility.store_locator.map_zoom_regional');
    } else if (zoom <= 12) {
      zoomDescription = this.translator.translate('accessibility.store_locator.map_zoom_city');
    } else if (zoom <= 15) {
      zoomDescription = this.translator.translate('accessibility.store_locator.map_zoom_local');
    } else {
      zoomDescription = this.translator.translate('accessibility.store_locator.map_zoom_street');
    }

    this.announceToScreenReader(`Map zoom: ${zoomDescription}`);
  }

  /**
   * Handle keyboard navigation for map with cluster support
   */
  handleMapKeydown(e) {
    if (!this.mapProvider) return;

    const panDistance = 200;
    const center = this.mapProvider.getCenter();
    const zoom = this.mapProvider.getZoom();

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        this.mapProvider.panBy([0, -panDistance]);
        this.announceToScreenReader(this.translator.translate('accessibility.store_locator.map_moved_up'));
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.mapProvider.panBy([0, panDistance]);
        this.announceToScreenReader(this.translator.translate('accessibility.store_locator.map_moved_down'));
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.mapProvider.panBy([-panDistance, 0]);
        this.announceToScreenReader(this.translator.translate('accessibility.store_locator.map_moved_left'));
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.mapProvider.panBy([panDistance, 0]);
        this.announceToScreenReader(this.translator.translate('accessibility.store_locator.map_moved_right'));
        break;
      case '+':
      case '=':
        e.preventDefault();
        this.mapProvider.zoomTo(zoom + 1);
        this.announceToScreenReader(this.translator.translate('accessibility.store_locator.map_zoom_in', { level: Math.round(zoom + 1) }));
        break;
      case '-':
        e.preventDefault();
        this.mapProvider.zoomTo(zoom - 1);
        this.announceToScreenReader(this.translator.translate('accessibility.store_locator.map_zoom_out', { level: Math.round(zoom - 1) }));
        break;
      case 'Home':
        e.preventDefault();
        this.resetMapView();
        this.announceToScreenReader(this.translator.translate('accessibility.store_locator.map_reset'));
        break;
      case 'Tab':
        // Allow tab navigation to cluster/marker elements
        this.handleTabNavigation(e);
        break;
      case 'Enter':
      case ' ':
        // Activate focused cluster or marker
        this.handleKeyboardActivation(e);
        break;
    }
  }

  /**
   * Handle tab navigation between clusters and markers
   */
  handleTabNavigation(e) {
    const features = this.mapProvider.queryRenderedFeatures({
      layers: ['clusters', 'unclustered-point']
    });

    if (features.length === 0) return;

    if (!this.keyboardFocusIndex) {
      this.keyboardFocusIndex = 0;
    } else {
      this.keyboardFocusIndex = e.shiftKey
        ? Math.max(0, this.keyboardFocusIndex - 1)
        : Math.min(features.length - 1, this.keyboardFocusIndex + 1);
    }

    const focusedFeature = features[this.keyboardFocusIndex];
    this.focusMapFeature(focusedFeature);

    e.preventDefault();
  }

  /**
   * Handle keyboard activation of clusters/markers
   */
  handleKeyboardActivation(e) {
    const features = this.mapProvider.queryRenderedFeatures({
      layers: ['clusters', 'unclustered-point']
    });

    if (features.length === 0 || this.keyboardFocusIndex === undefined) return;

    const focusedFeature = features[this.keyboardFocusIndex];

    if (focusedFeature.properties.cluster_id) {
      // Handle cluster activation via map provider
      this.mapProvider.handleClusterClick({ features: [focusedFeature] });
    } else {
      // Handle marker activation via map provider
      this.mapProvider.handleMarkerClick({ features: [focusedFeature] });
    }

    e.preventDefault();
  }

  /**
   * Focus on a specific map feature for keyboard navigation
   */
  focusMapFeature(feature) {
    if (!feature) return;

    const coordinates = feature.geometry.coordinates;

    // Pan to feature
    this.mapProvider.panTo(coordinates);

    // Announce focus change
    if (feature.properties.cluster_id) {
      const count = feature.properties.point_count;
      this.announceToScreenReader(this.translator.translate('accessibility.store_locator.map_focus_cluster', { count }));
    } else {
      const name = feature.properties.name;
      this.announceToScreenReader(this.translator.translate('accessibility.store_locator.map_focus_store', { name: name || 'store' }));
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Autocomplete search functionality
    if (this.searchInput) {
      // Input event for autocomplete
      this.searchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();

        if (query.length >= 3) {
          this.debouncedAutocomplete(query);
        } else {
          // Cancel any pending debounced calls
          this.debouncedAutocomplete.cancel();
          this.closeAutocomplete();
          // Clear results when query is too short
          this.autocompleteResults = [];
        }
      });

      // Focus event - reopen autocomplete if results exist
      this.searchInput.addEventListener('focus', () => {
        console.log('focus');

        const query = this.searchInput.value.trim();
        if (query.length >= 3 && this.autocompleteResults.length > 0) {
          this.openAutocomplete();
        }
      });

      // Keyboard navigation for autocomplete
      this.searchInput.addEventListener('keydown', (e) => {
        if (!this.isAutocompleteOpen) {
          // If autocomplete is closed and Enter is pressed, do nothing
          if (e.key === 'Enter') {
            e.preventDefault();
          }
          return;
        }

        // Only handle Escape - Tab navigation is handled natively by browser
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeAutocomplete();
        }
      });

      // Close autocomplete when clicking outside
      document.addEventListener('click', (e) => {
        if (!this.searchInput.contains(e.target) &&
          !this.autocompleteDropdown?.contains(e.target)) {
          this.closeAutocomplete();
        }
      });
    }

    // Search button - trigger autocomplete if there's a query
    if (this.searchButton) {
      this.searchButton.addEventListener('click', (e) => {
        e.preventDefault();
        const query = this.searchInput?.value?.trim();
        if (query && query.length >= 3) {
          this.debouncedAutocomplete(query);
        }
      });
    }

    // Clear search button
    if (this.clearSearchButton) {
      this.clearSearchButton.addEventListener('click', () => {
        this.clearSearch();
      });
    }

    // Geolocation button
    if (this.geolocationButton) {
      this.geolocationButton.addEventListener('click', () => {
        this.requestGeolocation();
      });
    }

    // Reset map button
    if (this.resetMapButton) {
      this.resetMapButton.addEventListener('click', () => {
        this.resetMap();
      });
    }

    // Filter controls
    const filterInputs = this.element.querySelectorAll('input[name="services"], input[name="features"]');
    filterInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.updateFilters();
      });
    });

    // Filter dropdown - close on outside click or escape
    if (this.filterDisclosure) {
      const filterSummary = this.filterDisclosure.querySelector('.filter-summary');

      // Close on Escape key
      this.filterDisclosure.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.filterDisclosure.open) {
          this.filterDisclosure.open = false;
        }
      });

      // Close on click outside
      document.addEventListener('click', (e) => {
        // Ignore if clicking on the summary (let native details/summary handle it)
        if (filterSummary && filterSummary.contains(e.target)) {
          return;
        }

        // Close if clicking outside the disclosure
        if (this.filterDisclosure.open && !this.filterDisclosure.contains(e.target)) {
          this.filterDisclosure.open = false;
        }
      });
    }

    // Pagination
    if (this.resultsContainer) {
      this.resultsContainer.addEventListener('click', (e) => {
        if (e.target.matches('.pagination-next')) {
          e.preventDefault();
          this.loadNextPage();
        }
      });
    }

    // Retry button
    this.element.addEventListener('click', (e) => {
      if (e.target.matches('.retry-button')) {
        this.loadInitialResults();
      }
    });

    // Mobile map toggle button
    if (this.mobileMapToggle) {
      this.mobileMapToggle.addEventListener('click', () => {
        this.toggleMobileMap();
      });
    }
  }

  /**
   * Toggle mobile map overlay
   */
  toggleMobileMap() {
    if (!this.mobileMapToggle) return;

    const isExpanded = this.mobileMapToggle.getAttribute('aria-expanded') === 'true';
    const newState = !isExpanded;

    // Update button state
    this.mobileMapToggle.setAttribute('aria-expanded', newState);

    // Toggle icons
    const showIcon = this.mobileMapToggle.querySelector('.icon-show-map');
    const hideIcon = this.mobileMapToggle.querySelector('.icon-hide-map');
    const buttonText = this.mobileMapToggle.querySelector('.toggle-button-text');

    // Toggle icon visibility - use setAttribute/removeAttribute for explicit control
    if (showIcon && hideIcon) {
      if (newState) {
        // Map is visible - show hide icon, hide show icon
        showIcon.setAttribute('hidden', '');
        hideIcon.removeAttribute('hidden');
      } else {
        // Map is hidden - show show icon, hide hide icon
        showIcon.removeAttribute('hidden');
        hideIcon.setAttribute('hidden', '');
      }
    }

    // Update button text and aria-label
    if (buttonText) {
      const showText = buttonText.getAttribute('data-show-text') || 'Show map';
      const hideText = buttonText.getAttribute('data-hide-text') || 'Hide map';
      const newText = newState ? hideText : showText;
      buttonText.textContent = newText;
      this.mobileMapToggle.setAttribute('aria-label', newText);
    }

    // Toggle map container visibility
    if (this.mapContainer) {
      const toggleContainer = this.mobileMapToggle.parentElement;

      if (newState) {
        // Calculate and set toggle container height for accurate map height calculation
        if (toggleContainer) {
          const toggleHeight = toggleContainer.offsetHeight;
          document.documentElement.style.setProperty('--mobile-toggle-height', `${toggleHeight}px`);
          toggleContainer.classList.add('mobile-map-toggle--active');
          toggleContainer.classList.add('mobile-map-visible');
        }

        this.mapContainer.classList.add('mobile-map-visible');

        // Show bakeries count
        if (this.mobileBakeriesCount) {
          this.mobileBakeriesCount.removeAttribute('hidden');
        }

        // Update all status displays (desktop + mobile overlay)
        this.updateStatus();

        // Resize map when shown
        setTimeout(() => {
          this.mapProvider?.resize();
        }, 300); // Match animation duration
      } else {
        if (toggleContainer) {
          toggleContainer.classList.remove('mobile-map-toggle--active');
          toggleContainer.classList.remove('mobile-map-visible');
        }

        this.mapContainer.classList.remove('mobile-map-visible');

        // Hide bakeries count
        if (this.mobileBakeriesCount) {
          this.mobileBakeriesCount.hidden = true;
        }
      }
    }

    // Prevent body scroll when map is open
    document.body.style.overflow = newState ? 'hidden' : '';
  }

  /**
   * Request user's geolocation
   */
  requestGeolocation() {
    if (!navigator.geolocation) {
      this.announceToScreenReader(this.translator.translate('accessibility.store_locator.geolocation_not_supported'));
      return;
    }

    if (this.geolocationButton) {
      this.geolocationButton.classList.add('loading');
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          if (this.geolocationButton) {
            this.geolocationButton.classList.remove('loading');
          }
          const { latitude, longitude } = position.coords;

          // Store user location for distance calculations
          this.currentFilters.userLocation = { lat: latitude, lng: longitude };

          // Update search input with translated message
          if (this.searchInput) {
            this.searchInput.value = this.translator.translate('store_locator.search.your_current_location');
          }

          // Show user location marker
          this.mapProvider?.addUserLocationMarker(latitude, longitude);

          // Update distance displays on all store cards
          this.updateAllDistances();

          // Zoom to user location
          if (this.mapProvider) {
            this.mapProvider.flyTo(latitude, longitude, this.searchZoom);
          }

          // Show reset map button
          this.toggleResetMapButton(true);

          // Viewport optimization will automatically update the store list
          this.announceToScreenReader(this.translator.translate('accessibility.store_locator.using_location'));
        } catch (error) {
          console.error('Error processing geolocation:', error);
          if (this.geolocationButton) {
            this.geolocationButton.classList.remove('loading');
          }
          this.announceToScreenReader(this.translator.translate('accessibility.store_locator.error_finding_stores'));
        }
      },
      (error) => {
        if (this.geolocationButton) {
          this.geolocationButton.classList.remove('loading');
        }
        console.error('Geolocation error:', error);

        let message = this.translator.translate('accessibility.store_locator.geolocation_error_prefix');
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message += this.translator.translate('accessibility.store_locator.geolocation_denied');
            break;
          case error.POSITION_UNAVAILABLE:
            message += this.translator.translate('accessibility.store_locator.geolocation_unavailable');
            break;
          case error.TIMEOUT:
            message += this.translator.translate('accessibility.store_locator.geolocation_timeout');
            break;
        }

        this.announceToScreenReader(message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  }

  /**
   * Clear search input and reset search state
   */
  clearSearch() {
    if (this.searchInput) {
      this.searchInput.value = '';
      this.searchInput.focus();
    }

    // Cancel any pending debounced calls
    this.debouncedAutocomplete.cancel();

    // Close autocomplete
    this.closeAutocomplete();
    this.autocompleteResults = [];

    // Clear search query filter
    this.currentFilters.searchQuery = '';

    // Announce to screen readers
    this.announceToScreenReader(this.translator.translate('accessibility.store_locator.search_cleared'));
  }

  /**
   * Reset map to default position and zoom
   */
  resetMap() {
    if (!this.mapProvider) return;

    // Reset to default coordinates and zoom
    this.mapProvider.flyTo(this.defaultLat, this.defaultLng, this.defaultZoom);

    // Clear user location filter
    this.currentFilters.userLocation = null;

    // Remove user location marker
    this.mapProvider.removeUserLocationMarker();

    // Hide all distance displays on store cards
    this.hideAllDistances();

    // Clear search input
    if (this.searchInput) {
      this.searchInput.value = '';
    }

    // Close autocomplete
    this.closeAutocomplete();
    this.autocompleteResults = [];

    // Hide reset map button
    this.toggleResetMapButton(false);

    // Announce to screen readers
    this.announceToScreenReader(this.translator.translate('accessibility.store_locator.map_reset'));
  }

  /**
   * Toggle reset map button visibility
   */
  toggleResetMapButton(show) {
    if (!this.resetMapButton) return;
    this.resetMapButton.hidden = !show;
  }

  /**
   * Load initial results without location
   */
  async loadInitialResults() {
    this.showLoadingState();
    await this.loadResults(true); // Reset cursor and fetch all locations
  }

  /**
   * Load results from Shopify Storefront API - fetches all locations
   * Search, filtering, and sorting handled on client-side
   */
  async loadResults(resetCursor = false) {
    if (this.isLoading) return;

    this.isLoading = true;

    try {
      // Load all locations using service (handles caching internally)
      this.allLocations = await this.locationsService.getAllLocations();

      // All locations loaded - apply client-side filtering and sorting
      this.applyFiltersAndSort();

      // Initial load: show all locations, don't auto-fit (use default zoom/center)
      this.updateMap(this.allLocations, false);

      // Manually trigger viewport update to populate visibleLocations
      // This ensures updateStatus() has correct viewport data on initial load
      if (this.mapProvider) {
        this.updateSidebarFromBounds();
      } else {
        // No map: render all filtered results
        this.renderResults(this.currentResults);
        this.renderPagination();
        this.updateStatus();
      }

    } catch (error) {
      console.error('Failed to load results:', error);
      this.showErrorState('Failed to load store locations. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Apply client-side filters and sorting to all locations
   */
  applyFiltersAndSort() {
    let filteredLocations = [...this.allLocations];

    // Apply search query filter
    if (this.currentFilters.searchQuery) {
      const queryLower = this.currentFilters.searchQuery.toLowerCase();
      filteredLocations = filteredLocations.filter(location => {
        return (
          location.name.toLowerCase().includes(queryLower) ||
          location.address.city.toLowerCase().includes(queryLower) ||
          location.address.province.toLowerCase().includes(queryLower) ||
          location.address.zip.toLowerCase().includes(queryLower)
        );
      });
    }

    // Apply service filters
    if (this.currentFilters.services.length > 0) {
      filteredLocations = this.filterByServices(filteredLocations);
    }

    // Apply feature filters
    if (this.currentFilters.features.length > 0) {
      filteredLocations = this.filterByFeatures(filteredLocations);
    }

    // Apply proximity sorting if user location is available
    if (this.currentFilters.userLocation) {
      filteredLocations = this.sortByDistance(filteredLocations, this.currentFilters.userLocation);
    }

    // Store all filtered results (for map and total count)
    this.filteredLocations = filteredLocations;

    // Paginate for list display (if enabled)
    if (this.showPagination) {
      const startIndex = 0;
      const endIndex = this.currentPage * this.resultsPerPage;
      this.currentResults = filteredLocations.slice(startIndex, endIndex);
    } else {
      // Show all filtered locations when pagination is disabled
      this.currentResults = filteredLocations;
    }
  }

  /**
   * Sort locations by distance from user location
   */
  sortByDistance(locations, userLocation) {
    // Filter out locations with invalid coordinates first
    const validLocations = locations.filter(loc => {
      const lat = loc.coordinates?.latitude;
      const lng = loc.coordinates?.longitude;
      return lat && lng && !isNaN(lat) && !isNaN(lng);
    });

    return validLocations.sort((a, b) => {
      const distanceA = this.calculateDistance(
        userLocation.lat,
        userLocation.lng,
        a.coordinates.latitude,
        a.coordinates.longitude
      );
      const distanceB = this.calculateDistance(
        userLocation.lat,
        userLocation.lng,
        b.coordinates.latitude,
        b.coordinates.longitude
      );
      return distanceA - distanceB;
    });
  }

  /**
   * Calculate distance between two coordinates using Haversine formula
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Radius of the Earth in km
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  }

  /**
   * Format distance for display based on unit system
   * Metric: meters if < 1km, otherwise km
   * Imperial: feet if < 1 mile, otherwise miles
   */
  formatDistance(distanceKm) {
    if (this.unitSystem === 'imperial') {
      // Convert km to miles (1 km = 0.621371 miles)
      const distanceMiles = distanceKm * 0.621371;

      if (distanceMiles < 1) {
        // Convert to feet (1 mile = 5280 feet)
        const feet = Math.round(distanceMiles * 5280);
        const formattedDistance = new Intl.NumberFormat(this.locale).format(feet);
        return this.translator.translate('general.distance.short', {
          distance: formattedDistance,
          unit_short: 'ft'
        });
      } else {
        const formattedDistance = new Intl.NumberFormat(this.locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(distanceMiles);
        return this.translator.translate('general.distance.long', {
          distance: formattedDistance,
          unit_long: 'miles'
        });
      }
    } else {
      // Metric system (default)
      if (distanceKm < 1) {
        const meters = Math.round(distanceKm * 1000);
        const formattedDistance = new Intl.NumberFormat(this.locale).format(meters);
        return this.translator.translate('general.distance.short', {
          distance: formattedDistance,
          unit_short: 'm'
        });
      } else {
        const formattedDistance = new Intl.NumberFormat(this.locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(distanceKm);
        return this.translator.translate('general.distance.long', {
          distance: formattedDistance,
          unit_long: 'km'
        });
      }
    }
  }

  /**
   * Update all distance displays on store cards
   */
  updateAllDistances() {
    if (!this.currentFilters.userLocation) return;

    const storeCards = this.element.querySelectorAll('.store-card');
    storeCards.forEach(card => {
      const distanceEl = card.querySelector('[data-store-distance]');
      const lat = parseFloat(card.dataset.latitude);
      const lng = parseFloat(card.dataset.longitude);

      if (distanceEl && lat && lng) {
        const distance = this.calculateDistance(
          this.currentFilters.userLocation.lat,
          this.currentFilters.userLocation.lng,
          lat,
          lng
        );

        // Only show distance if it's meaningful (> 50 meters)
        // This excludes cases where user selected this exact store as their location
        if (distance > 0.05) { // 0.05 km = 50 meters
          distanceEl.textContent = this.formatDistance(distance);
          distanceEl.hidden = false;
        } else {
          distanceEl.hidden = true;
        }
      }
    });
  }

  /**
   * Hide all distance displays on store cards
   */
  hideAllDistances() {
    const distanceElements = this.element.querySelectorAll('[data-store-distance]');
    distanceElements.forEach(el => {
      el.hidden = true;
    });
  }

  /**
   * Convert degrees to radians
   */
  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Convert camelCase/PascalCase to Title Case with spaces
   * Example: UberEats -> Uber Eats, DoorDash -> Door Dash, Menulog -> Menulog
   * @param {string} str - Service name to convert
   * @returns {string} Formatted display name
   */
  toTitleCase(str) {
    return str.replace(/([A-Z])/g, (match, letter, index) => {
      // Add space before capital letters, except at the start
      return index > 0 ? ` ${letter}` : letter;
    });
  }

  /**
   * Filter locations by delivery services
   */
  filterByServices(locations) {
    if (this.currentFilters.services.length === 0) {
      return locations;
    }

    return locations.filter(location => {
      const services = location.metafields.deliveryService;
      if (!services || typeof services !== 'object') {
        return false;
      }

      return this.currentFilters.services.some(filterService => {
        return services[filterService] && services[filterService].status === 'active';
      });
    });
  }

  /**
   * Filter locations by features
   */
  filterByFeatures(locations) {
    if (this.currentFilters.features.length === 0) {
      return locations;
    }

    return locations.filter(location => {
      // Check if location has all requested features
      return this.currentFilters.features.every(feature => {
        // Add feature checking logic here
        // For now, return true (no filtering)
        return true;
      });
    });
  }

  // calculateStoreStatus and calculateOpenStatus methods removed - now handled by LocationsService

  /**
   * Create individual store list item from template
   */
  createStoreListItem(store, index) {
    // Clone from template
    const clone = this.storeCardTemplate.content.cloneNode(true);
    const article = clone.querySelector('article');

    // Set listitem role
    article.setAttribute('role', 'listitem');

    // Populate with real store data
    this.populateStoreCard(article, store);

    return article;
  }

  /**
   * Populate store card with real data
   */
  populateStoreCard(article, store) {
    const storeName = store.metafields?.publicStoreName || store.name;
    const phone = store.address?.phone || store.phone;
    const address = store.address;
    const deliveryServices = store.metafields?.deliveryService || {};
    const status = store.status || { isOpen: false, statusText: 'Status Unknown' };

    // Use coordinates from transformed store object
    const lat = store.coordinates?.latitude;
    const lng = store.coordinates?.longitude;

    // Set data attributes
    article.dataset.storeId = store.id;
    article.dataset.latitude = lat;
    article.dataset.longitude = lng;

    // Store name and URLs
    const nameElements = article.querySelectorAll('[data-store-name]');
    nameElements.forEach(el => {
      el.textContent = escapeHtml(storeName);
    });

    // Generate URLs using slug
    const slug = store.slug || LocationsService.extractShortId(store.id);

    const urlElements = article.querySelectorAll('[data-store-url]');
    urlElements.forEach(el => {
      el.href = `/pages/bakeries/${slug}`;
    });

    const detailUrlElements = article.querySelectorAll('[data-store-detail-url]');
    detailUrlElements.forEach(el => {
      el.href = `/pages/bakeries/${slug}`;
    });

    // Status badge (Open/Closed/Temporarily Closed)
    const statusEl = article.querySelector('[data-store-status]');
    const {isOpen, isTemporarilyClosed, statusText, nextStateChangeData} = status;
    if (statusEl) {
      const displayText = isOpen
        ? this.translator.translate('location.status.open')
        : isTemporarilyClosed
          ? this.translator.translate('location.status.temporarily_closed')
          : this.translator.translate('location.status.closed');
      statusEl.textContent = displayText;
      statusEl.className = isOpen ? 'store-badge store-badge--open' : 'store-badge store-badge--closed';
    }

    // Closure reason (only shown when temporarily closed with custom reason)
    const closureReasonEl = article.querySelector('[data-store-closure-reason]');
    const hasClosureReason = isTemporarilyClosed && statusText && statusText !== this.translator.translate('location.status.temporarily_closed');
    
    if (closureReasonEl) {
      if (hasClosureReason) {
        closureReasonEl.textContent = statusText;
        closureReasonEl.hidden = false;
      } else {
        closureReasonEl.hidden = true;
      }
    }

    // Reopens day (next status change)
    const nextStatusEl = article.querySelector('[data-store-next-status]');
    if (nextStatusEl && nextStateChangeData && nextStateChangeData.key) {
      const translatedText = this.translator.translate(nextStateChangeData.key, nextStateChangeData.args);
      nextStatusEl.textContent = translatedText;
      nextStatusEl.hidden = false;
    } else if (nextStatusEl) {
      nextStatusEl.hidden = true;
    }

    // Phone
    const phoneEl = article.querySelector('[data-store-phone]');
    if (phoneEl && phone) {
      const phoneLink = phoneEl.querySelector('a') || phoneEl;
      phoneLink.href = `tel:${phone.replace(/\s/g, '')}`;
      const phoneText = phoneEl.querySelector('[data-store-phone-text]');
      if (phoneText) {
        phoneText.textContent = phone;
      }
      phoneEl.hidden = false;
    }

    // Address and Google Maps
    const addressEl = article.querySelector('[data-store-address]');
    if (addressEl && address) {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${address.address1} ${address.city} ${address.province} ${address.zip}`
      )}`;

      const mapsLink = addressEl.querySelector('[data-store-maps-url]') || addressEl;
      mapsLink.href = googleMapsUrl;

      const addressText = addressEl.querySelector('[data-store-address-text]');
      if (addressText) {
        const addressParts = [address.address1];
        if (address.address2) {
          addressParts.push(address.address2);
        }

        // Format city, state, zip line (handle missing state gracefully)
        const cityStateLine = address.province
          ? `${address.city}, ${address.provinceCode || address.province} ${address.zip}`
          : `${address.city} ${address.zip}`;

        addressParts.push(cityStateLine);
        addressText.textContent = addressParts.join(', ');
      }

      addressEl.hidden = false;
    }

    // Separate Get Directions link
    const directionsEl = article.querySelector('[data-store-directions]');
    if (directionsEl && address) {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${address.address1} ${address.city} ${address.province} ${address.zip}`
      )}`;

      directionsEl.href = googleMapsUrl;
      directionsEl.hidden = false;
    }

    // Delivery services
    if (deliveryServices && Object.keys(deliveryServices).length > 0) {
      const deliveryDesktop = article.querySelector('[data-delivery-services]');
      const deliveryMobile = article.querySelector('[data-delivery-services-mobile]');

      // Get bakery's timezone (fallback to default)
      const timezone = store.metafields?.timezone || this.defaultTimezone;
      const servicesHTML = this.renderDeliveryServices(deliveryServices, timezone);

      if (deliveryDesktop && servicesHTML) {
        deliveryDesktop.innerHTML = servicesHTML;
        deliveryDesktop.hidden = false;
      }

      if (deliveryMobile && servicesHTML) {
        deliveryMobile.innerHTML = servicesHTML;
        deliveryMobile.hidden = false;
      }
    }

    // Distance from user location (if set)
    const distanceEl = article.querySelector('[data-store-distance]');
    if (distanceEl && this.currentFilters.userLocation && store.coordinates) {
      const distance = this.calculateDistance(
        this.currentFilters.userLocation.lat,
        this.currentFilters.userLocation.lng,
        store.coordinates.latitude,
        store.coordinates.longitude
      );

      // Only show distance if it's meaningful (> 50 meters)
      // This excludes cases where user selected this exact store as their location
      if (distance > 0.05) { // 0.05 km = 50 meters
        distanceEl.textContent = this.formatDistance(distance);
        distanceEl.hidden = false;
      } else {
        distanceEl.hidden = true;
      }
    } else if (distanceEl) {
      distanceEl.hidden = true;
    }
  }


  /**
   * Render delivery services
   * @param {Object} services - Delivery services object
   * @param {string} timezone - Bakery's timezone for date comparisons
   */
  renderDeliveryServices(services, timezone) {
    if (!services || typeof services !== 'object') return '';

    const now = this.locationsService.getCurrentTime(timezone).getTime();

    const activeServices = Object.entries(services).filter(([serviceName, serviceData]) => {
      if (!serviceData || serviceData.status !== 'active') return false;

      // Check validity period (using bakery's timezone)
      if (serviceData.valid_from || serviceData.valid_to) {
        if (serviceData.valid_from) {
          const validFrom = this.locationsService.parseDate(serviceData.valid_from, timezone);
          if (validFrom && validFrom.getTime() > now) return false;
        }
        if (serviceData.valid_to) {
          const validTo = this.locationsService.parseDate(serviceData.valid_to, timezone);
          if (validTo && validTo.getTime() < now) return false;
        }
      }

      return true;
    });

    if (activeServices.length === 0) return '';

    const servicesHtml = activeServices.map(([serviceName, serviceData]) => {
      if (!serviceData.link) return '';
      // Get logo URL from theme settings, fallback to base URL pattern
      const serviceKey = serviceName.toLowerCase();
      let iconUrl = this.deliveryLogos[serviceKey];
      if (!iconUrl) {
        // Fallback to base URL pattern if not in theme settings
        // Special handling for SkipTheDishes (uses skip-square instead of skipthedishes)
        let logoName = serviceKey;
        if (serviceKey === 'skipthedishes') {
          logoName = 'skip-square';
        } else {
          // Add -square suffix for COBS brand fallback
          logoName = serviceKey + (this.brand === 'cobs' ? '-square' : '');
        }
        iconUrl = this.deliveryIconsBaseUrl.replace('menulog', logoName);
      }

      // Convert service name to title case with spaces (e.g., UberEats -> Uber Eats)
      const displayName = this.toTitleCase(serviceName);

      return `
        <a href="${escapeHtml(serviceData.link)}" class="delivery-service-link" target="_blank" rel="noopener noreferrer" aria-label="Order via ${escapeHtml(displayName)}">
          <img src="${escapeHtml(iconUrl)}" alt="${escapeHtml(displayName)}" class="delivery-service-logo delivery-service-${serviceName.toLowerCase()}" loading="lazy">
        </a>
      `;
    }).join('');

    return `<div class="delivery-services">${servicesHtml}</div>`;
  }

  /**
   * Focus on a specific store on the map with enhanced UX
   */
  focusStoreOnMap(store) {
    if (!this.mapProvider) return;

    const lng = store.coordinates?.longitude || store.lng;
    const lat = store.coordinates?.latitude || store.lat;

    // Set flag to prevent bounds update feedback loop
    this.isUpdatingFromBounds = true;

    // Progressive zoom to store detail level
    this.mapProvider.flyTo(lat, lng, 16);

    // Show popup after animation with slight delay
    setTimeout(() => {
      this.mapProvider.showPopup(store, [lng, lat]);

      // Reset bounds update flag
      this.isUpdatingFromBounds = false;
    }, 1600);

    // Announce action to screen readers
    this.announceToScreenReader(this.translator.translate('accessibility.store_locator.map_focusing_on_store', { name: store.name || store.title }));
  }

  // showStorePopup method removed - now handled by MapboxProvider

  /**
   * Update map with locations and optionally fit bounds
   * @param {Array} locations - Array of location objects to display on map
   * @param {boolean} fitBounds - Whether to fit map bounds to show all locations
   */
  updateMap(locations, fitBounds = false) {
    if (!this.mapProvider || !locations) return;

    // Add/update markers on map
    this.mapProvider.addMarkers(locations);

    // Fit map bounds if requested and locations exist
    if (fitBounds && locations.length > 0) {
      this.mapProvider.fitBounds(locations);
    }
  }

  /**
   * Update all status displays (desktop status + mobile overlay count)
   * Always based on current map viewport
   */
  updateStatus() {
    // Total bakeries in current viewport (after filters)
    // If visibleLocations is not set (map disabled or not initialized), use all filtered locations
    const totalInViewport = this.visibleLocations?.length ?? this.filteredLocations?.length ?? 0;

    // Currently displayed in the list (paginated)
    const displayedCount = this.currentResults?.length ?? 0;

    const term = totalInViewport === 1 ? this.translator.translate('store_locator.results.term_singular') : this.translator.translate('store_locator.results.term_plural');

    // 1. Update desktop/tablet results status text
    if (this.resultsStatus) {
      let statusText = '';

      if (totalInViewport === 0) {
        statusText = this.translator.translate('store_locator.empty.no_bakeries');
      } else if (displayedCount < totalInViewport) {
        statusText = this.translator.translate('store_locator.results.count_in_area', { displayed: displayedCount, total: totalInViewport, term });
      } else {
        statusText = this.translator.translate('store_locator.results.total_in_area', { total: totalInViewport, term });
      }

      this.resultsStatus.hidden = totalInViewport === 0;
      this.resultsStatus.textContent = statusText;
    }

    // 2. Update mobile map overlay count (only if visible)
    if (this.mobileBakeriesCount && !this.mobileBakeriesCount.hidden) {
      const countText = this.mobileBakeriesCount.querySelector('.bakeries-count-text');

      if (countText) {
        countText.textContent = totalInViewport
          ? this.translator.translate('store_locator.mobile.count_badge', { count: totalInViewport, term })
          : this.translator.translate('store_locator.mobile.no_bakeries');
      }
    }
  }

  /**
   * Show loading state with enhanced UX
   */
  showLoadingState(message = null) {
    message = message || this.translator.translate('store_locator.loading.stores');
    this.setStateVisibility({
      loading: true,
      error: false,
      empty: false,
      results: false
    });

    // Update loading message if provided
    const loadingText = this.element.querySelector('.loading-state p');
    if (loadingText) {
      loadingText.textContent = message;
    }

    // Show loading overlay on map during search
    this.showMapLoadingOverlay();

    // Announce loading to screen readers
    this.announceToScreenReader(message);
  }

  /**
   * Show map loading overlay
   */
  showMapLoadingOverlay() {
    if (!this.storeMap) return;

    // Remove existing overlay
    this.hideMapLoadingOverlay();

    this.mapLoadingOverlay = document.createElement('div');
    this.mapLoadingOverlay.className = 'map-loading-overlay';
    this.mapLoadingOverlay.innerHTML = `
      <div class="map-loading-content">
        <div class="loading-spinner" aria-label="Loading map data"></div>
        <p>Updating map...</p>
      </div>
    `;

    this.storeMap.appendChild(this.mapLoadingOverlay);
  }

  /**
   * Hide map loading overlay
   */
  hideMapLoadingOverlay() {
    if (this.mapLoadingOverlay) {
      this.mapLoadingOverlay.remove();
      this.mapLoadingOverlay = null;
    }
  }

  /**
   * Show error state with enhanced messaging
   */
  showErrorState(message, isRetryable = true) {
    const errorMessage = this.element.querySelector('.error-message h3');
    const errorDescription = this.element.querySelector('.error-message p');
    const retryButton = this.element.querySelector('.retry-button');

    if (errorMessage) {
      errorMessage.textContent = this.translator.translate('store_locator.error.unable_to_load');
    }

    if (errorDescription) {
      errorDescription.textContent = message || this.translator.translate('store_locator.error.try_again');
    }

    if (retryButton) {
      retryButton.style.display = isRetryable ? 'inline-block' : 'none';
    }

    this.setStateVisibility({
      loading: false,
      error: true,
      empty: false,
      results: false
    });

    // Hide map loading overlay
    this.hideMapLoadingOverlay();

    // Announce error to screen readers
    this.announceToScreenReader(`Error: ${message}`);
  }

  /**
   * Show empty state with contextual messaging
   */
  showEmptyState(message = null) {
    message = message || this.translator.translate('store_locator.empty.no_results');
    const emptyMessage = this.element.querySelector('.empty-message p');
    if (emptyMessage) {
      emptyMessage.textContent = message;
    }

    this.setStateVisibility({
      loading: false,
      error: false,
      empty: true,
      results: false
    });

    // Hide map loading overlay
    this.hideMapLoadingOverlay();

    // Announce empty state to screen readers
    this.announceToScreenReader(message);
  }

  /**
   * Hide loading state and show results
   */
  hideLoadingState() {
    this.setStateVisibility({
      loading: false,
      error: false,
      empty: false,
      results: true
    });

    // Hide map loading overlay
    this.hideMapLoadingOverlay();
  }

  /**
   * Set visibility of different UI states
   */
  setStateVisibility(states) {
    const stateElements = {
      loading: this.loadingState,
      error: this.errorState,
      empty: this.emptyState,
      results: this.resultsList
    };

    Object.entries(states).forEach(([state, visible]) => {
      const element = stateElements[state];
      if (element) {
        element.setAttribute('aria-hidden', !visible);
        // Don't override display for results list (uses flex from CSS)
        // For other states, use block display
        if (visible) {
          element.style.display = '';  // Remove inline style, use CSS
        } else {
          element.style.display = 'none';
        }
      }
    });
  }

  /**
   * Update filters based on user selection
   */
  updateFilters() {
    const serviceInputs = this.element.querySelectorAll('input[name="services"]:checked');
    const featureInputs = this.element.querySelectorAll('input[name="features"]:checked');

    this.currentFilters.services = Array.from(serviceInputs).map(input => input.value);
    this.currentFilters.features = Array.from(featureInputs).map(input => input.value);
    this.currentPage = 1; // Reset pagination

    // Update filter count badge
    this.updateFilterCountBadge();

    // Apply filters on already loaded locations
    this.applyFiltersAndSort();

    // Filter update: show filtered locations, fit bounds to show all results
    this.updateMap(this.filteredLocations, false);

    // Update sidebar from bounds to recalculate visibleLocations and update status
    if (this.mapProvider) {
      this.updateSidebarFromBounds();
    } else {
      // No map: render all filtered results
      this.renderResults(this.currentResults);
      this.renderPagination();
      this.updateStatus();
    }
  }

  /**
   * Update filter count badge
   */
  updateFilterCountBadge() {
    const badge = this.element.querySelector('.filter-count-badge');
    if (!badge) return;

    const totalFilters = this.currentFilters.services.length + this.currentFilters.features.length;
    const countNumber = badge.querySelector('.filter-count-number');

    if (totalFilters > 0) {
      countNumber.textContent = totalFilters;
      badge.hidden = false;
    } else {
      badge.hidden = true;
    }
  }

  /**
   * Generate delivery service filter options dynamically based on available services in locations
   */
  generateDeliveryServiceFilters() {
    const filterContainer = this.element.querySelector('[data-filter-type="services"]');
    const filterGroup = this.element.querySelector('#delivery-services-filter');

    if (!filterContainer || !this.allLocations) return;

    // Extract all unique delivery services from locations
    const deliveryServices = new Set();
    this.allLocations.forEach(location => {
      const services = location.metafields?.deliveryService;

      // deliveryService is an object with service names as keys
      if (services && typeof services === 'object' && !Array.isArray(services)) {
        Object.keys(services).forEach(serviceName => {
          const serviceInfo = services[serviceName];
          // Only include active services
          if (serviceInfo && serviceInfo.status === 'active') {
            deliveryServices.add(serviceName);
          }
        });
      }
    });

    // Sort services alphabetically
    const sortedServices = Array.from(deliveryServices).sort();

    // Clear existing content
    filterContainer.innerHTML = '';

    // Generate checkbox for each service
    if (sortedServices.length > 0) {
      // Show the filter group
      if (filterGroup) {
        filterGroup.style.display = '';
      }

      sortedServices.forEach(service => {
        const label = document.createElement('label');
        label.className = 'filter-option';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.name = 'services';
        checkbox.value = service;
        checkbox.addEventListener('change', () => this.updateFilters());

        const customCheckbox = document.createElement('span');
        customCheckbox.className = 'checkbox-custom';
        customCheckbox.setAttribute('aria-hidden', 'true');

        const labelText = document.createElement('span');
        labelText.className = 'filter-label';
        // Convert service name to display format (e.g., UberEats -> Uber Eats)
        labelText.textContent = this.toTitleCase(service);

        label.appendChild(checkbox);
        label.appendChild(customCheckbox);
        label.appendChild(labelText);
        filterContainer.appendChild(label);
      });
    } else {
      // No delivery services found - hide the entire filter group
      if (filterGroup) {
        filterGroup.style.display = 'none';
      }
    }
  }

  /**
   * Load next page of results using cursor
   */
  async loadNextPage() {
    if (!this.hasNextPage || !this.currentCursor || this.isLoading) return;

    this.showLoadingState();
    await this.loadResults(false);

    // Scroll to top of results
    this.resultsContainer?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // getBrandPrimaryColor method removed - now handled by MapboxProvider

  /**
   * Reset map to default view
   */
  resetMapView() {
    if (!this.mapProvider) return;

    this.mapProvider.setCenter(this.defaultLat, this.defaultLng, this.defaultZoom);
  }

  /**
   * Announce message to screen readers
   */
  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'visually-hidden';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const storeLocatorElement = document.querySelector('.store-locator');
  if (storeLocatorElement) {
    new StoreLocator(storeLocatorElement);
  }
});
